import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import styles from "./styles";
import { Controller, useForm } from "react-hook-form";
import { object, RefinementCtx, string, z, ZodIssueCode } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import InputField from "../../components/InputField";
import CustomSelect from "../../components/SelectField";
import { useEffect, useState } from "react";
import { IRole, IUserFormData } from "./types";
import getAPIUrl from "../../config";
import {
  getApi,
  postApiWithAuth,
  postApiWithAuthFormData,
} from "../../utils/apis";
import {
  getApiErrorMessage,
  getUserClientId,
  getUserRole,
} from "../../utils/commonHelpers";
import { useNavigate } from "react-router-dom";
import User from "../../assets/user.svg";
import { generateRandomPassword } from "../../utils";
import { IClient } from "../Clients/types";
import { ILocation } from "../Locations/types";
import Header from "../Header";
import toastStyles from "../../components/Toast/styles";
import Toast from "../../components/Toast";
import { useTranslation } from "react-i18next";
import { MuiTelInput } from "mui-tel-input";

const AddUser = () => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [clients, setClients] = useState<IClient[]>([]);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [errorToast, setErrorToast] = useState<boolean>(false);
  const [toastErrorMsg, setErrorToastMsg] = useState<string>("");
  const [successToast, setSuccessToast] = useState<boolean>(false);
  const [toastSuccessMsg, setSuccessToastMsg] = useState<string>("");

  const navigate = useNavigate();

  const { t } = useTranslation();

  const userRole = getUserRole();
  const userCLientId = getUserClientId();
  const receptionistUserTypeId = roles.filter(
    (rec) => rec.name === "Receptionist"
  )[0]?.id;

  const validationSchema = object({
    firstName: string().min(1, "Please provide First Name"),
    lastName: string().min(1, "Please provide Last Name"),
    email: string({ required_error: "Please provide an email" }).email({
      message: "Please provide a valid email address",
    }),
    phone: z
      .string()
      .regex(
        /^\+48\d{9}$/,
        "Phone number must be in the format +48 followed by 9 digits"
      ),
    //phone: z.string().regex(/^\+\d{2} \d{3} \d{3} \d{3}$/, "Phone number must be in the format +48 923 922 332"),
    //phone: string().optional(),
    roleId: string().min(1, "Please select User Type"),
    password: string().regex(
      /^(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[#!@$%^&*]).{8,}$/,
      "Passwords must be at least 8 characters long and contain at least 1 capital letter, special character and number"
    ),
    profileImage: z
      .instanceof(File, { message: "Please upload profile pic" })
      .optional(),
    clientId: string().optional(),
    locationId: string().optional(),
  }).superRefine((val, ctx: RefinementCtx) => {
    if (val.roleId === receptionistUserTypeId && !val.clientId) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: "Please select client",
        path: ["clientId"],
      });
    }
    if (val.roleId === receptionistUserTypeId && !val.locationId) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: "Please select location",
        path: ["locationId"],
      });
    }
  });

  const defaultValues: IUserFormData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    roleId: "",
    password: "",
    clientId: "",
    locationId: "",
  };

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(validationSchema),
  });

  const uploadFile = async (fileToUpload: File) => {
    try {
      const formData = new FormData();
      formData.append("files", fileToUpload);

      const res = await postApiWithAuthFormData(
        `${getAPIUrl()}/api/v1/files/user-images`,
        formData
      );
      return res.data;
    } catch (error) {
      if (error instanceof Error) {
        const errorMsg = getApiErrorMessage(error);
        setErrorToastMsg(errorMsg);
        setErrorToast(true);
      }
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    const { profileImage, ...rest } = data;
    let imageUrl = "";
    if (profileImage) {
      const uploadedImage = await uploadFile(profileImage);
      imageUrl = uploadedImage[0].name;
    }
    try {
      await postApiWithAuth(`${getAPIUrl()}/api/v1/users`, {
        ...rest,
        imageUrl,
      });

      setSuccessToastMsg("User created successfully");
      setSuccessToast(true);
      reset(defaultValues);
      setTimeout(() => {
        setLoading(false);
        navigate("/users");
      }, 1000);
    } catch (error) {
      if (error instanceof Error) {
        const errorMsg = getApiErrorMessage(error);
        setErrorToastMsg(errorMsg);
        setErrorToast(true);
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    const getRoles = async () => {
      try {
        const rolesData = await getApi(`${getAPIUrl()}/api/v1/roles/name`);
        setRoles(rolesData.data);
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          const errorMsg = getApiErrorMessage(error);
          setErrorToast(true);
          setErrorToastMsg(errorMsg);
        }
      }
    };
    const getClients = async () => {
      try {
        const clientsData = await getApi(`${getAPIUrl()}/api/v1/clients/name`);
        setClients(clientsData.data);
        if (userRole === "Client Admin") {
          setValue("clientId", userCLientId || "");
        }
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          const errorMsg = getApiErrorMessage(error);
          setErrorToast(true);
          setErrorToastMsg(errorMsg);
        }
      }
    };
    const getLocations = async () => {
      try {
        const locationsData = await getApi(
          `${getAPIUrl()}/api/v1/locations/name`
        );
        setLocations(locationsData.data);
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          const errorMsg = getApiErrorMessage(error);
          setErrorToast(true);
          setErrorToastMsg(errorMsg);
        }
      }
    };
    getRoles();
    getClients();
    getLocations();
  }, [setValue, userCLientId, userRole]);

  const rolesList =
    roles && roles.map((role) => ({ name: role.name, value: role.id }));
  const clientsList =
    clients &&
    clients.map((client) => ({ name: client.name, value: client.id }));

  const handleGenerateRandomPassword = () => {
    const randomPassword = generateRandomPassword();
    setValue("password", randomPassword);
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters except the leading +
    const digits = value.replace(/[^\d+]/g, "");
    // Ensure it starts with +48 and is followed by 9 digits
    if (!digits.startsWith("+48")) {
      return "+48";
    }
    return `+48${digits.slice(3, 12)}`;
  };

  const handleCancel = () => {
    reset(defaultValues);
    navigate("/users");
  };

  const watchFields = watch(["roleId", "clientId"]);

  return (
    <Grid sx={styles.container}>
      <Header
        name={t("newUser")}
        setErrorToast={setErrorToast}
        setSuccessToast={setSuccessToast}
        setErrorToastMsg={setErrorToastMsg}
        setSuccessToastMsg={setSuccessToastMsg}
      />

      <Box sx={styles.userDetailsContainer}>
        <Typography sx={styles.subHeading}>{t("userDetails")}</Typography>
        {loading ? (
          <Typography sx={styles.spinner} data-testId="loader">
            <CircularProgress />
          </Typography>
        ) : (
          <Box component="form" sx={styles.formContainer} onSubmit={onSubmit}>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 10 }}>
              <Grid item md={6} xs={12}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="firstName"
                      label={t("firstName")}
                      placeholder={t("firstName")}
                      type="text"
                      showCross
                      fullWidth
                      size="small"
                      handleCrossClick={() => setValue("firstName", "")}
                      error={!!errors.firstName?.message}
                      errorMessage={errors.firstName?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="lastName"
                      label={t("lastName")}
                      placeholder={t("lastName")}
                      type="text"
                      showCross
                      fullWidth
                      size="small"
                      handleCrossClick={() => setValue("lastName", "")}
                      error={!!errors.lastName?.message}
                      errorMessage={errors.lastName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <>
                      <MuiTelInput
                        {...field}
                        id="phone"
                        label={t("phone")}
                        placeholder={t("phone")}
                        fullWidth
                        defaultCountry="PL"
                        preferredCountries={["PL"]}
                        size="small"
                        forceCallingCode
                        disableDropdown
                        value={formatPhoneNumber(field.value)}
                        onChange={(newValue) => {
                          const formattedValue = formatPhoneNumber(newValue);
                          setValue("phone", formattedValue, {
                            shouldValidate: true,
                          });
                          field.onChange(formattedValue);
                        }}
                        error={!!errors.phone?.message}
                      />
                      {errors.phone && (
                        <FormHelperText error>
                          {errors.phone.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="email"
                      label={t("email")}
                      placeholder={t("email")}
                      type="text"
                      showCross
                      fullWidth
                      size="small"
                      handleCrossClick={() => setValue("email", "")}
                      error={!!errors.email?.message}
                      errorMessage={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Controller
                  name="roleId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      id="roleId"
                      onChange={(e) => field.onChange(e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={t("userType")}
                      items={rolesList}
                      error={!!errors.roleId?.message}
                      errorMessage={errors.roleId?.message}
                      label={t("userType")}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Controller
                  name="profileImage"
                  control={control}
                  render={({ field }) => (
                    <Grid sx={styles.profileImageGrid}>
                      <img
                        src={
                          field.value ? URL.createObjectURL(field.value) : User
                        }
                        alt="profile"
                        style={styles.profileImage}
                      />
                      <Button component="label" sx={styles.uploadImageButton}>
                        {t("uploadImage")}
                        <input
                          type="file"
                          id="upload-profile-picture"
                          accept=".png,.jpg,.jpeg"
                          hidden
                          onChange={(e) =>
                            field.onChange(
                              e.target.files && (e.target.files[0] as File)
                            )
                          }
                        />
                      </Button>
                    </Grid>
                  )}
                />
              </Grid>

              {watchFields[0] !== "" &&
                roles.filter((rec) => rec.id === watchFields[0])[0].name !==
                  "Admin" && (
                  <>
                    <Grid item md={6} xs={12}>
                      <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect
                            {...field}
                            id="clientId"
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={t("client")}
                            items={clientsList}
                            error={!!errors.clientId?.message}
                            errorMessage={errors.clientId?.message}
                            label={t("client")}
                            disabled={userRole === "Client Admin"}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Controller
                        name="locationId"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect
                            {...field}
                            id="locationId"
                            onChange={(e) => field.onChange(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={t("location")}
                            items={
                              watchFields[1] === ""
                                ? []
                                : locations
                                    .filter(
                                      (rec) => rec.clientId === watchFields[1]
                                    )
                                    .map((location) => ({
                                      name: location.name,
                                      value: location.id,
                                    }))
                            }
                            error={!!errors.locationId?.message}
                            errorMessage={errors.locationId?.message}
                            label={t("location")}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
            </Grid>

            <Typography sx={styles.passwordText}>{t("password")}</Typography>

            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 10 }}>
              <Grid item md={6} xs={12}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="password"
                      label={t("password")}
                      placeholder={t("password")}
                      type="password"
                      showPasswordToggle
                      showCross
                      fullWidth
                      size="small"
                      handleCrossClick={() => setValue("password", "")}
                      error={!!errors.password?.message}
                      errorMessage={errors.password?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Button
                  sx={styles.generatePasswordButton}
                  onClick={handleGenerateRandomPassword}
                >
                  {t("generateRandom")}
                </Button>
              </Grid>
            </Grid>

            <Grid
              container
              rowSpacing={2}
              columnSpacing={{ xs: 1, sm: 10 }}
              sx={styles.buttonsGrid}
            >
              <Grid item md={6} xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={styles.cancelButton}
                  onClick={handleCancel}
                >
                  {t("cancel")}
                </Button>
              </Grid>

              <Grid item md={6} xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={styles.saveButton}
                >
                  {t("save")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      <Toast
        id="add-user-success"
        open={successToast}
        message={toastSuccessMsg}
        severity="success"
        onClose={() => setSuccessToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        alertStyles={toastStyles.toastText}
      />
      <Toast
        id="add-user-error"
        open={errorToast}
        message={toastErrorMsg}
        severity="error"
        onClose={() => setErrorToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        alertStyles={toastStyles.toastText}
      />
    </Grid>
  );
};

export default AddUser;
